import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import AuthContext from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Base from '../components/Base'; // Assuming Base component exists
import axios from 'axios';
import { Card, Button, Badge, Spinner, Skeleton, Alert, EmptyState } from '../components/ui';

// Base URL for images, ensuring proper loading of Django media files
const API_BASE_URL_FOR_IMAGES = process.env.REACT_APP_API_BASE_URL || 'https://vasstrinfo.com';
const HomePage = () => {
  const navigate = useNavigate();
  const { authToken, logoutUser, user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
  });
  const [slideIndexes, setSlideIndexes] = useState({}); // State for image slider indexes for each product
  
  // useRef is kept as it might be used internally by Base or other components not shown.

  const authAxios = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'https://vasstrinfo.com/api',
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  }), [authToken?.access]);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await authAxios.get('/get_subscription_status/');
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  }, [authAxios]);

  const fetchProducts = useCallback(async () => {
    if (!authToken?.access) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authAxios.get('/products/'); 
      
      if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
        const initialSlideIndexes = {};
        response.data.products.forEach((product, idx) => {
            initialSlideIndexes[idx] = 1; // Start each product's slider at 1
        });
        setSlideIndexes(initialSlideIndexes);

      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching products:', err.response || err);
      setError('Failed to load products. Please try again later.');
      if (err.response?.status === 401) {
        Swal.fire({
          title: 'Session Expired',
          text: 'Please login again',
          icon: 'warning',
        }).then(() => {
          logoutUser();
          navigate('/login');
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, authToken?.access, navigate, logoutUser]);

  useEffect(() => {
    if (authToken) {
      fetchSubscriptionStatus();
      fetchProducts();
    } else {
      navigate('/login');
    }
  }, [authToken, navigate, fetchSubscriptionStatus, fetchProducts]);

  // This function is no longer actively used by the image rendering,
  // as imageUrls array is built directly within the map. Keeping for reference.
  const getProductImageUrl = (product) => {
    if (product.img1) {
      if (product.img1.startsWith('http')) {
        return product.img1;
      }
      return `${API_BASE_URL_FOR_IMAGES}${product.img1}`;
    }
    return 'https://placehold.co/150x150/EEEEEE/333333?text=No+Image';
  };

  const toggleStock = async (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    try {
      const response = await authAxios.post(`/toggle_stock/${productId}/`);
      if (response.data.success) {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === productId
              ? { ...product, in_stock: response.data.in_stock }
              : product
          )
        );
        Swal.fire(
          'Success!',
          `Product is now ${response.data.in_stock ? 'In Stock' : 'Out of Stock'}.`,
          'success'
        );
      } else {
        Swal.fire('Error!', 'Failed to toggle stock status.', 'error');
      }
    } catch (err) {
      console.error('Error toggling stock:', err.response || err);
      Swal.fire('Error!', 'An error occurred while toggling stock status.', 'error');
    }
  };

  const confirmDelete = (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await authAxios.delete(`/delete_product/${productId}/`);
          Swal.fire('Deleted!', 'The product has been deleted.', 'success');
          setProducts((prevProducts) =>
            prevProducts.filter((product) => product.id !== productId)
          );
        } catch (err) {
          console.error('Error deleting product:', err.response || err);
          Swal.fire('Error!', 'Failed to delete the product.', 'error');
        }
      }
    });
  };

  const handleCopyUrl = (url) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.onmouseenter = Swal.stopTimer;
          toast.onmouseleave = Swal.resumeTimer;
        }
      });
      Toast.fire({
        icon: "success",
        title: "Product URL copied to clipboard!"
      });
    }).catch(err => {
      console.error('Failed to copy URL:', err);
      Swal.fire('Error', 'Failed to copy URL. Please try manually.', 'error');
    });
  };

  const handleEdit = (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    navigate(`/EditProduct/${productId}`);
  };

  const handleUploadProduct = (e) => {
    if (!subscriptionStatus.is_active) {
      e.preventDefault();
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
    }
  };

  const plusSlides = (n, productIndex) => {
    setSlideIndexes((prev) => {
      const newIndexes = { ...prev };
      const currentProductSlideIndex = newIndexes[productIndex] || 1;
      const slides = document.querySelectorAll(`#slideshow-container-${productIndex} .mySlides`);
      
      if (!slides || slides.length === 0) return newIndexes;

      let nextSlideIndex = currentProductSlideIndex + n;

      if (nextSlideIndex > slides.length) nextSlideIndex = 1;
      if (nextSlideIndex < 1) nextSlideIndex = slides.length;
      
      newIndexes[productIndex] = nextSlideIndex;
      return newIndexes;
    });
  };

  const currentSlide = (n, productIndex) => {
    setSlideIndexes((prev) => ({
      ...prev,
      [productIndex]: n,
    }));
  };

  // Memoized showSlides to be stable across renders
  const showSlides = useCallback((productIndex) => {
    const slides = document.querySelectorAll(`#slideshow-container-${productIndex} .mySlides`);
    const dots = document.querySelectorAll(`#slideshow-container-${productIndex} .dot`);
    
    if (!slides || slides.length === 0) return;

    slides.forEach((slide) => (slide.style.display = 'none'));
    dots.forEach((dot) => {
      dot.classList.remove('bg-[#717171]');
      dot.classList.add('bg-[#bbb]');
    });

    const currentProductSlideIndex = slideIndexes[productIndex] || 1;
    if (slides[currentProductSlideIndex - 1]) {
      slides[currentProductSlideIndex - 1].style.display = 'block';
    }
    if (dots.length > 0 && dots[currentProductSlideIndex - 1]) {
      dots[currentProductSlideIndex - 1].classList.remove('bg-[#bbb]');
      dots[currentProductSlideIndex - 1].classList.add('bg-[#717171]');
    }
  }, [slideIndexes]); // Dependency on slideIndexes for useCallback


  // Effect to trigger showSlides when slideIndexes or products change
  useEffect(() => {
    products.forEach((_, index) => {
      if (slideIndexes[index] !== undefined) { // Only call if index is initialized
        showSlides(index);
      }
    });
  }, [products, slideIndexes, showSlides]);


  if (isLoading) {
    return (
      <Base Content={
        <div className="flex justify-center items-center h-screen">
          <p className="text-xl text-gray-700">Loading your products...</p>
        </div>
      } />
    );
  }

  if (error) {
    return (
      <Base Content={
        <div className="flex justify-center items-center h-screen">
          <p className="text-xl text-red-500">{error}</p>
        </div>
      } />
    );
  }

  return (
    <Base
      Content={
        <div className="min-h-screen px-2 sm:px-4 py-8 max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6 px-4 sm:px-0">
            <h1 className="text-2xl md:text-3xl font-bold text-orange-600">
              Your Products
              <div className="line left-line w-2/5 h-px bg-gray-300 justify-self-center"></div>
            </h1>
            <div className="justify-self-end">
              <a
                href={user?.username ? `https://${user.username}.vasstr.com` : '#'} 
                className="group flex items-center gap-2 text-orange-600 hover:text-orange-700 transition"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="flex items-center gap-1 text-sm bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full shadow-sm">
                  Designer Portfolio
                </span>
              </a>
              <p className="text-xs text-gray-500 mt-1 text-right">Tap here to explore your portfolio</p>
            </div>
          </div>

          {!subscriptionStatus.is_active && (
            <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 mb-6 mx-auto max-w-full rounded-md">
              <p className="font-semibold">
                Your subscription has expired. Subscribe now to manage your products!
              </p>
              <Link to="/subscription">
                <button className="mt-2 bg-orange-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-orange-700 transition">
                  Subscribe Now
                </button>
              </Link>
            </div>
          )}

          <div className="p-5 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
            <div
              className="flex flex-col items-center justify-center gap-1 p-5 rounded-lg transition-transform border-2 min-h-full border-dotted border-[#c9764f] 
                         flex-shrink-1 h-[460px] cursor-pointer"
            >
              <Link
                to="/UploadProduct"
                onClick={handleUploadProduct}
                className={`flex flex-col items-center justify-center w-full h-full text-[#c9764f] hover:text-[#f97316] 
                           ${subscriptionStatus.is_active ? 'hover:shadow-md' : 'opacity-50 cursor-not-allowed'}`} 
              >
                <div className="text-[80px]">+</div>
                <p className="text-lg font-bold">Upload Product</p>
              </Link>
            </div>

            {products.map((product, index) => {
              // Collect all available image URLs for the slider, filtering out null/empty strings
              const imageUrls = [product.img1, product.img2, product.img3, product.img4, product.img5, product.img6]
                                .filter(Boolean) // Remove null, undefined, empty strings
                                .map(imgUrl => {
                                  // Prepend base URL only if it's a relative path
                                  if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://') || imgUrl.startsWith('/media/')) {
                                      return imgUrl;
                                  }
                                  return `${API_BASE_URL_FOR_IMAGES}${imgUrl}`;
                                });

              return (
                // Reverted the product card styling to match the original
                <div
                    key={product.id} 
                    className="flex flex-col items-center justify-start gap-2 p-3 rounded-lg shadow-md bg-white 
                               flex-shrink-1 h-[460px] transform transition duration-300 hover:scale-102 hover:shadow-lg"
                >
                    <div
                        id={`slideshow-container-${index}`}
                        // Reverted image container styling to match the original
                        className="relative w-full h-4/5 overflow-hidden rounded-lg" 
                    >
                        {imageUrls.length > 1 && (
                            <button
                                title="Previous Image"
                                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full bg-white/60 text-[#d25614] hover:bg-white/80 hover:text-[#a8430e] shadow-md hover:shadow-lg transition-all duration-150 ease-in-out transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614]"
                                onClick={() => plusSlides(-1, index)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-left"><path d="m15 18-6-6 6-6"/></svg>
                            </button>
                        )}

                        {imageUrls.length > 0 ? (
                            imageUrls.map( (img, idx) => (
                                <div key={idx} className="mySlides hidden flex items-center justify-center w-full h-full">
                                    <img
                                        src={img}
                                        alt={`Product ${idx + 1}`}
                                        className="w-full h-full object-cover rounded-lg"
                                        onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/150x150/EEEEEE/333333?text=Image+Load+Error'; }}
                                    />
                                </div>
                            ))
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100 rounded-lg">No Image</div>
                        )}

                        {imageUrls.length > 1 && (
                            <button
                                title="Next Image"
                                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full bg-white/60 text-[#d25614] hover:bg-white/80 hover:text-[#a8430e] shadow-md hover:shadow-lg transition-all duration-150 ease-in-out transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614]"
                                onClick={() => plusSlides(1, index)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-right"><path d="m9 18 6-6-6-6"/></svg>
                            </button>
                        )}
                
                        {imageUrls.length > 1 && (
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                                {imageUrls.map((_, i) => (
                                <button
                                    key={i}
                                    aria-label={`Go to slide ${i + 1}`}
                                    className={`dot w-2 h-2 rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614] ${(slideIndexes[index] || 1) === i + 1 ? 'bg-[#717171]' : 'bg-[#bbb]'}`}
                                    onClick={() => currentSlide(i + 1, index)}
                                ></button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="w-full text-center mt-2">
                        <p className="text-[#c9764f] font-semibold text-base truncate" title={product.name}>
                            {product.name}
                        </p>
                        <p className="text-sm !font-lora">₹{parseFloat(product.price).toLocaleString('en-IN')}</p>
                        {product.Discountedprice && (
                            <p className="text-sm text-green-600">
                                Discounted: ₹{parseFloat(product.Discountedprice).toLocaleString('en-IN')}
                            </p>
                        )}
                    </div>

                    <div className="flex justify-center items-center gap-4 mt-1">
                        <button
                            onClick={() => handleCopyUrl(product.external_url)}
                            disabled={!subscriptionStatus.is_active}
                            title="Copy product URL"
                            className={`text-[#d25614] hover:text-[#a8430e] ${
                                !subscriptionStatus.is_active ? 'opacity-50 cursor-not-allowed' : ''
                            } focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614]`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-copy"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2v2"/></svg>
                        </button>
                        <a
                            href={product.external_url}
                            onClick={(e) => {
                                if (!subscriptionStatus.is_active) {
                                    e.preventDefault();
                                    Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
                                }
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Preview product"
                            className={`text-[#d25614] hover:text-[#a8430e] ${
                                !subscriptionStatus.is_active ? 'opacity-50 cursor-not-allowed' : ''
                            } focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614]`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                        </a>
                    </div>

                    <div className="w-full grid grid-cols-2 gap-2 mt-2">
                        <button
                            onClick={() => toggleStock(product.id)}
                            disabled={!subscriptionStatus.is_active}
                            // Reverted toggle stock button styling
                            className={`col-span-2 rounded text-white text-sm h-[30px] transition-all duration-200 ${
                                product.in_stock ? 'bg-[#c9764f]' : 'bg-gray-500'
                            } ${
                                subscriptionStatus.is_active
                                ? 'hover:shadow hover:-translate-y-0.5'
                                : 'opacity-50 cursor-not-allowed'
                            } focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614]`}
                        >
                            {product.in_stock ? 'Make Out of Stock' : 'Make In Stock'}
                        </button>
                        <button
                            onClick={() => handleEdit(product.id)}
                            disabled={!subscriptionStatus.is_active}
                            // Reverted Edit/Delete button styling
                            className={`border border-[#d25614] text-[#d25614] rounded text-sm h-[30px] ${
                                subscriptionStatus.is_active ? 'hover:bg-[#f5f7f2]' : 'opacity-50 cursor-not-allowed'
                            } focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614]`}
                        >
                            Edit
                        </button>
                        <button
                            onClick={() => confirmDelete(product.id)}
                            disabled={!subscriptionStatus.is_active}
                            // Reverted Edit/Delete button styling
                            className={`border border-[#d25614] text-[#d25614] rounded text-sm h-[30px] ${
                                subscriptionStatus.is_active ? 'hover:bg-[#f5f7f2]' : 'opacity-50 cursor-not-allowed'
                            } focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[#d25614]`}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            );
            })}
          </div>

          {products.length === 0 && !isLoading && (
            <div className="text-center text-gray-500 my-8 col-span-full">
                No products found. Start by uploading your first product!
            </div>
          )}
        </div>
      }
    />
  );
};

export default HomePage;